import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { getOrAnalyzeMarket, isStale, getRating, tierColor, tierLabel, BASE_RATES, RATE_LABELS, RATE_UNITS } from '../lib/marketRates';
import { fmt$ } from '../lib/pricing';
import { v3 } from '../lib/styleTokens';
import { AISparkle } from '../components/v3';

export default function MarketRatesPage() {
  const { isAdmin } = useAuth();
  return (
    <div style={{ height:'100%', overflowY:'auto', padding:'24px', background:v3.color.bg, fontFamily:v3.font.family }}>
      <MarketRatesContent isAdmin={isAdmin} />
    </div>
  );
}

// ─── Market Rates Admin ───────────────────────────────────────────────────────
function MarketRatesContent({ isAdmin }) {
  // Clean zip to 5 digits for display and search
  const zip5 = z => { const d = z ? String(z).replace(/\D/g, '').slice(0, 5) : ''; return d; };
  const [markets,      setMarkets]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [search,       setSearch]       = useState('');
  const [filterTier,   setFilterTier]   = useState('all');
  const [expanded,     setExpanded]     = useState(null);
  const [analyzing,    setAnalyzing]    = useState(null);
  const [newMarket,    setNewMarket]    = useState({ zip: '', city: '', state: '' });
  const [showNew,      setShowNew]      = useState(false);
  const [newLoading,   setNewLoading]   = useState(false);
  const [newMsg,       setNewMsg]       = useState('');
  const [editRates,    setEditRates]    = useState(null);
  const [saving,       setSaving]       = useState(false);


  useEffect(() => { loadMarkets(); }, []);

  async function loadMarkets() {
    setLoading(true);
    const { data } = await supabase
      .from('market_rate_analyses')
      .select('*')
      .order('state', { ascending: true })
      .order('city', { ascending: true });
    setMarkets(data || []);
    setLoading(false);
  }

  async function refreshMarket(market) {
    setAnalyzing(market.id);
    try {
      const cleanZip = zip5(market.zip) || null;
      await getOrAnalyzeMarket(cleanZip, true, market.city, market.state);
      await loadMarkets();
    } catch (e) {
      alert('Refresh failed: ' + e.message);
    }
    setAnalyzing(null);
  }

  async function deleteMarket(market) {
    if (!window.confirm(`Delete ${market.city}, ${market.state}? This will remove all stored rate data. The market will be re-analyzed fresh next time a quote uses this location.`)) return;
    const { error } = await supabase.from('market_rate_analyses').delete().eq('id', market.id);
    if (error) { alert('Delete failed: ' + error.message); return; }
    await loadMarkets();
  }

  async function analyzeNew() {
    if (!newMarket.zip && (!newMarket.city || !newMarket.state)) {
      setNewMsg('Enter a zip code, or city + state.');
      return;
    }
    setNewLoading(true); setNewMsg('');
    try {
      await getOrAnalyzeMarket(newMarket.zip || null, true, newMarket.city || null, newMarket.state || null);
      setNewMsg('✓ Analysis complete');
      setNewMarket({ zip: '', city: '', state: '' });
      await loadMarkets();
      setTimeout(() => { setShowNew(false); setNewMsg(''); }, 1500);
    } catch (e) {
      setNewMsg('✗ ' + e.message);
    }
    setNewLoading(false);
  }

  async function saveRateOverrides(market) {
    if (!editRates) return;
    setSaving(true);
    try {
      await supabase.from('market_rate_analyses')
        .update({ rates: editRates, updated_at: new Date().toISOString() })
        .eq('id', market.id);
      await loadMarkets();
      setExpanded(null);
      setEditRates(null);
    } catch (e) {
      alert('Save failed: ' + e.message);
    }
    setSaving(false);
  }

  const filtered = markets.filter(m => {
    const q = search.toLowerCase();
    const zipQ = q.replace(/\D/g, '');
    const allZips = m.zip_codes?.length > 0 ? m.zip_codes : (zip5(m.zip) ? [zip5(m.zip)] : []);
    const matchSearch = !q
      || (m.city?.toLowerCase() ?? '').includes(q)
      || (m.state?.toLowerCase() ?? '').includes(q)
      || (zipQ.length >= 2 && allZips.some(z => z.includes(zipQ)));
    const matchTier = filterTier === 'all' || m.market_tier === filterTier;
    return matchSearch && matchTier;
  });

  const staleCount = markets.filter(m => isStale(m.analyzed_at)).length;

  return (
    <div>
      {/* Analyzing status banner — genuine AI (marketAnalysis); AISparkle earned here */}
      {analyzing && (
        <div style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', background:v3.color.inkBg, border:`1px solid ${v3.color.inkBorder}`, borderRadius:v3.radius.card, marginBottom:12 }}>
          <span style={{ display:'inline-block', width:14, height:14, border:`2px solid ${v3.color.inkBorder}`, borderTopColor:v3.color.ink, borderRadius:'50%', animation:'spin 0.7s linear infinite', flexShrink:0 }}/>
          <AISparkle size={14} color={v3.color.ink} />
          <span style={{ fontSize:v3.font.size.label, color:v3.color.ink, fontWeight:v3.font.weight.semibold }}>
            Running AI market analysis — this may take 15–30 seconds...
          </span>
        </div>
      )}

      {/* Spin keyframe injected once */}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <h2 style={{ fontSize: v3.font.size.pageTitle, fontWeight: v3.font.weight.semibold, color: v3.color.body, margin: 0, letterSpacing: v3.font.letterSpacing.tight }}>Market Intelligence</h2>
          <div style={{ fontSize: v3.font.size.label, color: v3.color.muted, marginTop: 4 }}>
            {markets.length} markets · {staleCount > 0 && <span style={{ color: v3.color.status.amberText, fontWeight: v3.font.weight.semibold }}>{staleCount} stale (3+ months — refresh needed)</span>}
            {staleCount === 0 && markets.length > 0 && <span style={{ color: v3.color.status.greenText }}>All analyses current</span>}
          </div>
        </div>
        <button onClick={() => setShowNew(!showNew)}
          style={{ padding: '9px 16px', background: v3.color.ink, color: 'white', border: 'none', borderRadius: v3.radius.input, fontSize: v3.font.size.label, fontWeight: v3.font.weight.semibold, cursor: 'pointer' }}>
          + Analyze New Market
        </button>
      </div>

      {/* New market panel */}
      {showNew && (
        <div style={{ background: v3.color.cardAlt, border: `1px solid ${v3.color.border}`, borderRadius: v3.radius.cardLarge, padding: 16, marginBottom: 16 }}>
          <div style={{ fontSize: v3.font.size.body, fontWeight: v3.font.weight.semibold, color: v3.color.body, marginBottom: 10 }}>Analyze New Market</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div>
              <label style={{ display: 'block', fontSize: v3.font.size.micro, fontWeight: v3.font.weight.semibold, color: v3.color.bodySoft, marginBottom: 3 }}>ZIP Code</label>
              <input value={newMarket.zip} onChange={e => setNewMarket(m => ({ ...m, zip: e.target.value }))}
                placeholder="e.g. 90210"
                style={{ width: 110, padding: v3.space.inputPad, border: `1px solid ${v3.color.border}`, borderRadius: v3.radius.input, fontSize: v3.font.size.body, outline: 'none', color: v3.color.body }} />
            </div>
            <div style={{ fontSize: v3.font.size.small, color: v3.color.mutedSoft, paddingBottom: 10 }}>or</div>
            <div>
              <label style={{ display: 'block', fontSize: v3.font.size.micro, fontWeight: v3.font.weight.semibold, color: v3.color.bodySoft, marginBottom: 3 }}>City</label>
              <input value={newMarket.city} onChange={e => setNewMarket(m => ({ ...m, city: e.target.value }))}
                placeholder="e.g. Austin"
                style={{ width: 150, padding: v3.space.inputPad, border: `1px solid ${v3.color.border}`, borderRadius: v3.radius.input, fontSize: v3.font.size.body, outline: 'none', color: v3.color.body }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: v3.font.size.micro, fontWeight: v3.font.weight.semibold, color: v3.color.bodySoft, marginBottom: 3 }}>State</label>
              <input value={newMarket.state} onChange={e => setNewMarket(m => ({ ...m, state: e.target.value.toUpperCase().slice(0,2) }))}
                placeholder="TX"
                style={{ width: 56, padding: v3.space.inputPad, border: `1px solid ${v3.color.border}`, borderRadius: v3.radius.input, fontSize: v3.font.size.body, outline: 'none', textTransform: 'uppercase', color: v3.color.body }} />
            </div>
            <button onClick={analyzeNew} disabled={newLoading}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 18px', background: v3.color.ink, color: 'white', border: 'none', borderRadius: v3.radius.input, fontSize: v3.font.size.label, fontWeight: v3.font.weight.semibold, cursor: 'pointer', opacity: newLoading ? 0.6 : 1 }}>
              {!newLoading && <AISparkle size={13} color="#FFFFFF" />}
              {newLoading ? 'Analyzing...' : 'Run Analysis'}
            </button>
            <button onClick={() => { setShowNew(false); setNewMsg(''); }}
              style={{ padding: '9px 14px', background: v3.color.card, border: `1px solid ${v3.color.border}`, borderRadius: v3.radius.input, fontSize: v3.font.size.label, color: v3.color.muted, cursor: 'pointer' }}>
              Cancel
            </button>
          </div>
          {newMsg && (
            <div style={{ marginTop: 8, fontSize: v3.font.size.small, fontWeight: v3.font.weight.semibold, color: newMsg.startsWith('✓') ? v3.color.status.greenText : v3.color.status.redText }}>{newMsg}</div>
          )}
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search city, state, or zip..."
          style={{ flex: 1, maxWidth: 280, padding: v3.space.inputPad, border: `1px solid ${v3.color.border}`, borderRadius: v3.radius.input, fontSize: v3.font.size.body, outline: 'none', color: v3.color.body }} />
        <select value={filterTier} onChange={e => setFilterTier(e.target.value)}
          style={{ padding: v3.space.inputPad, border: `1px solid ${v3.color.border}`, borderRadius: v3.radius.input, fontSize: v3.font.size.body, background: v3.color.card, outline: 'none', color: v3.color.body }}>
          <option value="all">All Tiers</option>
          <option value="secondary">Secondary</option>
          <option value="adjusted">Adjusted</option>
          <option value="standard">Standard</option>
          <option value="premium">Premium</option>
        </select>
        <div style={{ fontSize: v3.font.size.small, color: v3.color.mutedSoft, alignSelf: 'center' }}>{filtered.length} markets</div>
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 32, color: v3.color.mutedSoft, fontSize: v3.font.size.body }}>Loading markets...</div>
      ) : (
        <div style={{ background: v3.color.card, border: `1px solid ${v3.color.border}`, borderRadius: v3.radius.cardLarge, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: v3.color.cardAlt }}>
                {['Market', 'Tier', 'CoL Index', 'Remote', 'On-Site Block', 'Dev/CRM', 'Last Analyzed', 'Source', ''].map(h => (
                  <th key={h} style={{ padding: v3.space.cellPad, textAlign: 'left', fontSize: v3.font.size.micro, fontWeight: v3.font.weight.semibold, color: v3.color.muted, borderBottom: `1px solid ${v3.color.border}`, textTransform: 'uppercase', letterSpacing: v3.font.letterSpacing.uppercase, whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(m => {
                const stale = isStale(m.analyzed_at);
                const isExpanded = expanded === m.id;
                const rates = m.rates || {};
                return (
                  <>
                    <tr key={m.id}
                      style={{ borderBottom: `1px solid ${v3.color.borderSoft}`, background: isExpanded ? v3.color.cardAlt : v3.color.card, cursor: 'pointer' }}
                      onClick={() => { setExpanded(isExpanded ? null : m.id); setEditRates(isExpanded ? null : { ...rates }); }}>
                      <td style={{ padding: v3.space.cellPad }}>
                        <div style={{ fontSize: v3.font.size.body, fontWeight: v3.font.weight.semibold, color: v3.color.body }}>{m.city}, {m.state}</div>
                        {/* All known zip codes as tags */}
                        {(m.zip_codes?.length > 0 || m.zip) && (
                          <div style={{ display:'flex', gap:3, flexWrap:'wrap', marginTop:3 }}>
                            {(m.zip_codes?.length > 0 ? m.zip_codes : [zip5(m.zip)]).filter(Boolean).map(z => (
                              <span key={z} style={{ fontSize:v3.font.size.micro, fontFamily:v3.font.mono, background:v3.color.cardAlt, color:v3.color.bodySoft, padding:'1px 5px', borderRadius:v3.radius.pill, border:`1px solid ${v3.color.border}` }}>{z}</span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: v3.space.cellPad }}>
                        <span style={{ fontSize: v3.font.size.micro, fontWeight: v3.font.weight.bold, color: tierColor(m.market_tier), background: tierColor(m.market_tier) + '18', padding: '2px 8px', borderRadius: v3.radius.pill }}>
                          {tierLabel(m.market_tier)}
                        </span>
                      </td>
                      <td style={{ padding: v3.space.cellPad, fontSize: v3.font.size.small, fontFamily: v3.font.mono, color: v3.color.bodySoft }}>{m.col_index}</td>
                      <td style={{ padding: v3.space.cellPad, fontSize: v3.font.size.small, fontFamily: v3.font.mono, color: v3.color.bodySoft }}>{fmt$(rates.remote_support)}/hr</td>
                      <td style={{ padding: v3.space.cellPad, fontSize: v3.font.size.small, fontFamily: v3.font.mono, color: v3.color.bodySoft }}>{fmt$(rates.onsite_block_2hr)}</td>
                      <td style={{ padding: v3.space.cellPad, fontSize: v3.font.size.small, fontFamily: v3.font.mono, color: v3.color.bodySoft }}>{fmt$(rates.dev_crm)}/hr</td>
                      <td style={{ padding: v3.space.cellPad }}>
                        <div style={{ fontSize: v3.font.size.small, color: stale ? v3.color.status.amberText : v3.color.muted, fontWeight: stale ? v3.font.weight.semibold : v3.font.weight.normal }}>
                          {new Date(m.analyzed_at).toLocaleDateString()}
                          {stale && ' ⚠'}
                        </div>
                      </td>
                      <td style={{ padding: v3.space.cellPad }}>
                        <span style={{ fontSize: v3.font.size.micro, color: v3.color.mutedSoft, textTransform: 'uppercase', letterSpacing: v3.font.letterSpacing.uppercase }}>{m.analysis_source}</span>
                      </td>
                      <td style={{ padding: '9px 10px' }}>
                        {isAdmin && (
                          <button
                            onClick={e => { e.stopPropagation(); deleteMarket(m); }}
                            style={{ fontSize: v3.font.size.small, padding: '4px 10px', background: v3.color.status.redBg, border: `1px solid ${v3.color.status.redText}22`, borderRadius: v3.radius.input, cursor: 'pointer', color: v3.color.status.redText, fontWeight: v3.font.weight.semibold, whiteSpace: 'nowrap' }}>
                            Delete
                          </button>
                        )}
                        <button
                          onClick={e => { e.stopPropagation(); refreshMarket(m); }}
                          disabled={analyzing === m.id}
                          style={{ fontSize: v3.font.size.small, padding: '4px 10px', background: analyzing === m.id ? v3.color.inkBg : v3.color.card, border: `1px solid ${analyzing === m.id ? v3.color.inkBorder : v3.color.border}`, borderRadius: v3.radius.input, cursor: analyzing === m.id ? 'default' : 'pointer', color: analyzing === m.id ? v3.color.ink : v3.color.body, fontWeight: v3.font.weight.semibold, whiteSpace: 'nowrap', display:'flex', alignItems:'center', gap:4 }}>
                          {analyzing === m.id ? (
                            <>
                              <span style={{ display:'inline-block', width:8, height:8, border:`2px solid ${v3.color.inkBorder}`, borderTopColor:v3.color.ink, borderRadius:'50%', animation:'spin 0.7s linear infinite' }}/>
                              Analyzing...
                            </>
                          ) : '↻ Refresh'}
                        </button>
                      </td>
                    </tr>

                    {/* Expanded detail row */}
                    {isExpanded && editRates && (
                      <tr key={m.id + '_detail'}>
                        <td colSpan={9} style={{ padding: '0 0 12px 0', borderBottom: `1px solid ${v3.color.border}`, background: v3.color.cardAlt }}>
                          <div style={{ padding: '12px 16px' }}>

                            {/* Rate edit grid */}
                            <div style={{ fontSize: v3.font.size.micro, fontWeight: v3.font.weight.semibold, textTransform: 'uppercase', color: v3.color.muted, letterSpacing: v3.font.letterSpacing.uppercase, marginBottom: 10 }}>
                              Rates — edit to override, then save
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 12 }}>
                              {Object.entries(RATE_LABELS).map(([key, label]) => {
                                const marketRate = editRates[key];
                                const rating = getRating(BASE_RATES[key], marketRate);
                                return (
                                  <div key={key} style={{ background: v3.color.card, border: `1px solid ${v3.color.border}`, borderRadius: v3.radius.card, padding: '8px 10px' }}>
                                    <div style={{ fontSize: v3.font.size.small, color: v3.color.muted, marginBottom: 4 }}>{label}</div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                      <span style={{ fontSize: v3.font.size.small, color: v3.color.muted }}>$</span>
                                      <input
                                        type="number"
                                        value={editRates[key] || ''}
                                        onChange={e => setEditRates(r => ({ ...r, [key]: parseFloat(e.target.value) || 0 }))}
                                        style={{ width: 76, padding: '4px 6px', border: `1px solid ${v3.color.border}`, borderRadius: v3.radius.input, fontSize: v3.font.size.small, fontFamily: v3.font.mono, fontWeight: v3.font.weight.semibold, color: v3.color.body, outline: 'none' }}
                                      />
                                      <span style={{ fontSize: v3.font.size.small, color: v3.color.mutedSoft }}>{RATE_UNITS[key]}</span>
                                    </div>
                                    {rating && (
                                      <div style={{ fontSize: v3.font.size.micro, fontWeight: v3.font.weight.bold, color: rating.color, marginTop: 3 }}>
                                        {rating.label} vs published
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>

                            {/* ZIP Codes */}
                            <div style={{ marginBottom:10 }}>
                              <div style={{ fontSize:v3.font.size.micro, fontWeight:v3.font.weight.semibold, textTransform:'uppercase', letterSpacing:v3.font.letterSpacing.uppercase, color:v3.color.muted, marginBottom:4 }}>ZIP Codes Mapped to This Market</div>
                              <div style={{ display:'flex', gap:4, flexWrap:'wrap' }}>
                                {(m.zip_codes?.length > 0 ? m.zip_codes : zip5(m.zip) ? [zip5(m.zip)] : []).map(z => (
                                  <span key={z} style={{ fontSize:v3.font.size.small, fontFamily:v3.font.mono, background:v3.color.cardAlt, color:v3.color.bodySoft, padding:'3px 8px', borderRadius:v3.radius.pill, border:`1px solid ${v3.color.border}`, fontWeight:v3.font.weight.semibold }}>{z}</span>
                                ))}
                                {!m.zip_codes?.length && !m.zip && (
                                  <span style={{ fontSize:v3.font.size.small, color:v3.color.mutedSoft, fontStyle:'italic' }}>No zips on file — analyzed by city/state only</span>
                                )}
                              </div>
                            </div>

                            {/* Pricing multiplier & market notes */}
                            <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
                              <div style={{ background: v3.color.inkBg, borderRadius: v3.radius.card, padding: '10px 14px', flex: '0 0 auto' }}>
                                <div style={{ fontSize: v3.font.size.small, color: v3.color.muted, marginBottom: 2 }}>Pricing Multiplier</div>
                                <div style={{ fontSize: v3.font.size.header, fontWeight: v3.font.weight.bold, fontFamily: v3.font.mono, color: v3.color.ink }}>{m.pricing_multiplier}x</div>
                                <div style={{ fontSize: v3.font.size.micro, color: v3.color.inkSoft }}>Managed IT package adjustment</div>
                              </div>
                              <div style={{ background: v3.color.card, border: `1px solid ${v3.color.border}`, borderRadius: v3.radius.card, padding: '10px 14px', flex: 1 }}>
                                <div style={{ fontSize: v3.font.size.micro, color: v3.color.muted, marginBottom: 2, fontWeight: v3.font.weight.semibold, textTransform: 'uppercase', letterSpacing: v3.font.letterSpacing.uppercase }}>Market Intelligence</div>
                                <div style={{ fontSize: v3.font.size.label, color: v3.color.bodySoft, lineHeight: 1.5 }}>{m.market_notes || '—'}</div>
                              </div>
                            </div>

                            {/* Actions */}
                            <div style={{ display: 'flex', gap: 8 }}>
                              <button onClick={() => saveRateOverrides(m)} disabled={saving}
                                style={{ padding: '8px 16px', background: v3.color.ink, color: 'white', border: 'none', borderRadius: v3.radius.input, fontSize: v3.font.size.label, fontWeight: v3.font.weight.semibold, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
                                {saving ? 'Saving...' : 'Save Rate Changes'}
                              </button>
                              <button onClick={() => refreshMarket(m)} disabled={analyzing === m.id}
                                style={{ padding: '8px 14px', background: analyzing === m.id ? v3.color.inkBg : v3.color.card, border: `1px solid ${analyzing === m.id ? v3.color.inkBorder : v3.color.border}`, borderRadius: v3.radius.input, fontSize: v3.font.size.label, color: analyzing === m.id ? v3.color.ink : v3.color.body, fontWeight: v3.font.weight.semibold, cursor: analyzing === m.id ? 'default' : 'pointer', display:'flex', alignItems:'center', gap:6 }}>
                                {analyzing === m.id ? (
                                  <>
                                    <span style={{ display:'inline-block', width:10, height:10, border:`2px solid ${v3.color.inkBorder}`, borderTopColor:v3.color.ink, borderRadius:'50%', animation:'spin 0.7s linear infinite' }}/>
                                    Re-analyzing market...
                                  </>
                                ) : <><AISparkle size={13} color="currentColor" /> Re-run AI Analysis</>}
                              </button>
                              <button onClick={() => { setExpanded(null); setEditRates(null); }}
                                style={{ padding: '8px 12px', background: v3.color.card, border: `1px solid ${v3.color.border}`, borderRadius: v3.radius.input, fontSize: v3.font.size.label, color: v3.color.muted, cursor: 'pointer' }}>
                                Close
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
              {filtered.length === 0 && !loading && (
                <tr><td colSpan={9} style={{ padding: 32, textAlign: 'center', color: '#9ca3af', fontSize: 12 }}>
                  No markets found. Use "Analyze New Market" to add one.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
